/** Un menú flotante al estilo de Notion: se abre junto a lo que lo pulsa, se cierra al hacer
 * clic fuera o con Escape, y se mueve con las flechas.
 *
 * Se pinta con `position: fixed` y las coordenadas del elemento que lo abre, para que ninguna
 * columna con `overflow: hidden` (la barra lateral) lo recorte.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'

export interface Opcion {
  texto: string
  icono?: string
  atajo?: string
  peligro?: boolean
  separador?: boolean
  alElegir?: () => void
}

export function Flotante({
  ancla,
  alCerrar,
  ancho = 240,
  alinear = 'inicio',
  children,
}: {
  ancla: RefObject<HTMLElement | null>
  alCerrar: () => void
  ancho?: number
  alinear?: 'inicio' | 'fin'
  children: ReactNode
}) {
  const caja = useRef<HTMLDivElement>(null)
  const [sitio, setSitio] = useState<{ left: number; top: number } | null>(null)

  useLayoutEffect(() => {
    const el = ancla.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const alto = caja.current?.offsetHeight ?? 0
    let left = alinear === 'fin' ? r.right - ancho : r.left
    left = Math.max(8, Math.min(left, window.innerWidth - ancho - 8))
    let top = r.bottom + 4
    // Si no cabe debajo, se abre hacia arriba.
    if (alto && top + alto > window.innerHeight - 8) top = Math.max(8, r.top - alto - 4)
    setSitio({ left, top })
  }, [ancla, ancho, alinear])

  useEffect(() => {
    const fuera = (e: MouseEvent) => {
      const dentro = caja.current?.contains(e.target as Node)
      const enAncla = ancla.current?.contains(e.target as Node)
      if (!dentro && !enAncla) alCerrar()
    }
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        alCerrar()
      }
    }
    document.addEventListener('mousedown', fuera)
    document.addEventListener('keydown', tecla, true)
    return () => {
      document.removeEventListener('mousedown', fuera)
      document.removeEventListener('keydown', tecla, true)
    }
  }, [ancla, alCerrar])

  return createPortal(
    <div
      ref={caja}
      className="flotante"
      role="menu"
      style={{
        width: ancho,
        left: sitio?.left ?? -9999,
        top: sitio?.top ?? -9999,
        visibility: sitio ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>,
    document.body,
  )
}

export function Menu({
  ancla,
  opciones,
  alCerrar,
  ancho,
  alinear,
}: {
  ancla: RefObject<HTMLElement | null>
  opciones: Opcion[]
  alCerrar: () => void
  ancho?: number
  alinear?: 'inicio' | 'fin'
}) {
  return (
    <Flotante ancla={ancla} alCerrar={alCerrar} ancho={ancho} alinear={alinear}>
      <div className="menu">
        {opciones.map((o, i) =>
          o.separador ? (
            <div className="menu__separador" key={i} role="separator" />
          ) : (
            <button
              key={i}
              role="menuitem"
              className={`menu__opcion${o.peligro ? ' menu__opcion--peligro' : ''}`}
              onClick={() => {
                alCerrar()
                o.alElegir?.()
              }}
            >
              <span className="menu__icono" aria-hidden="true">{o.icono}</span>
              <span className="menu__texto">{o.texto}</span>
              {o.atajo && <span className="menu__atajo">{o.atajo}</span>}
            </button>
          ),
        )}
      </div>
    </Flotante>
  )
}
